// iter142b — Driver QR scan landing page.
// Public, no auth — a driver who scans the customer's QR (encoded at
// /ride/customer/:rideId) lands here. They see redacted customer info
// (first name, destination, time, masked phone) and a "Sign up as a
// Vendor" form (Name + WhatsApp). Submitting creates a vendor row and
// saves the two-way customer↔vendor relationship server-side.
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, Car, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";

function fmtRideTime(iso, lang) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(lang === "es" ? "es-CU" : "en-US", {
      hour: "2-digit", minute: "2-digit", month: "short", day: "numeric",
    });
  } catch { return iso || ""; }
}

export default function RideCustomerScan() {
  const { rideId } = useParams();
  const { lang } = useI18n();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [whatsapp, setWhatsapp] = useState("+53 ");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get(`/rides/${rideId}/customer-card`)
      .then(({ data }) => { if (!cancelled) { setCard(data); setDone(!!data.already_signed_up); } })
      .catch(() => { if (!cancelled) setCard(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [rideId]);

  const submit = async () => {
    const driver_name = name.trim();
    const digits = whatsapp.replace(/\D/g, "");
    if (driver_name.length < 2) {
      toast.error(lang === "es" ? "Ingresa tu nombre" : "Enter your name");
      return;
    }
    if (digits.length < 8) {
      toast.error(lang === "es" ? "Número de WhatsApp inválido" : "Invalid WhatsApp number");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/rides/${rideId}/driver-signup`, {
        driver_name, whatsapp: digits,
      });
      setDone(true);
      toast.success(lang === "es"
        ? (data?.duplicate ? "Ya estás registrado" : "¡Registro exitoso!")
        : (data?.duplicate ? "You're already signed up" : "Signed up successfully!"));
    } catch (e) {
      const msg = e?.response?.data?.detail || (lang === "es" ? "No se pudo registrar" : "Could not sign up");
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen grid place-items-center" style={{ background: "#0B0B0B" }}>
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: "rgba(245,241,232,0.4)" }} />
      </main>
    );
  }
  if (!card) {
    return (
      <main className="min-h-screen grid place-items-center px-6" style={{ background: "#0B0B0B" }}
        data-testid="ride-scan-not-found">
        <div className="text-center">
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, color: "#F5F1E8" }}>
            {lang === "es" ? "Viaje no encontrado" : "Ride not found"}
          </h1>
          <p className="mt-3" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#B0AAA3" }}>
            {lang === "es"
              ? "Este código QR no es válido o ha expirado."
              : "This QR code is invalid or has expired."}
          </p>
          <Link to="/" className="mt-6 inline-block"
            style={{ color: "#E8B84B", fontFamily: "'DM Sans', sans-serif", fontSize: 14,
                     letterSpacing: "0.04em", textDecoration: "underline" }}>
            {lang === "es" ? "Ir a Habano" : "Open Habano"}
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="ride-scan-page" className="min-h-screen pb-16" style={{ background: "#0B0B0B" }}>
      <header className="px-5 pt-10 text-center">
        <div className="inline-flex items-center gap-2"
          style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#E8B84B",
                   letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 600 }}>
          <Car className="w-4 h-4" strokeWidth={1.5} />
          {lang === "es" ? "Habano · Conductor" : "Habano · Driver"}
        </div>
        <h1 className="mt-3" data-testid="ride-scan-title"
          style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, color: "#F5F1E8", lineHeight: 1.15 }}>
          {lang === "es" ? "Confirma este viaje" : "Confirm this ride"}
        </h1>
      </header>

      {/* Customer card */}
      <section className="mx-5 mt-8" data-testid="ride-scan-customer-card"
        style={{ background: "#171717", border: "1px solid rgba(232,184,75,0.3)",
                 borderRadius: 16, padding: "22px 22px 18px" }}>
        <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#B0AAA3",
                       letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600 }}>
          {lang === "es" ? "Cliente" : "Customer"}
        </div>
        <div className="mt-1" data-testid="ride-scan-customer-name"
          style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#F5F1E8" }}>
          {card.customer_first_name || (lang === "es" ? "Huésped" : "Guest")}
        </div>

        <div className="mt-5">
          <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#B0AAA3",
                         letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600 }}>
            {lang === "es" ? "Destino" : "Destination"}
          </div>
          <div className="mt-1" data-testid="ride-scan-destination"
            style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: "#F5F1E8", fontWeight: 500 }}>
            {card.destination_name || "—"}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-4">
          <div>
            <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#B0AAA3",
                           letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600 }}>
              {lang === "es" ? "Hora" : "Time"}
            </div>
            <div className="mt-1" data-testid="ride-scan-time"
              style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#F5F1E8" }}>
              {fmtRideTime(card.ride_time, lang)}
            </div>
          </div>
          <div>
            <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#B0AAA3",
                           letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600 }}>
              {lang === "es" ? "Teléfono" : "Phone"}
            </div>
            <div className="mt-1" data-testid="ride-scan-phone-masked"
              style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#F5F1E8" }}>
              {card.phone_masked || "—"}
            </div>
          </div>
        </div>
      </section>

      {done ? (
        // Confirmation state
        <section className="mx-5 mt-6 text-center" data-testid="ride-scan-done"
          style={{ background: "#171717", border: "1px solid rgba(232,184,75,0.3)",
                   borderRadius: 16, padding: "28px 22px" }}>
          <CheckCircle2 className="w-10 h-10 mx-auto" style={{ color: "#E8B84B" }} strokeWidth={1.5} />
          <h2 className="mt-3"
            style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#F5F1E8" }}>
            {lang === "es" ? "¡Estás registrado!" : "You're signed up!"}
          </h2>
          <p className="mt-2 mx-auto" style={{ maxWidth: 320,
            fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#B0AAA3", lineHeight: 1.5 }}>
            {lang === "es"
              ? "Tu información se guardó y este cliente ahora puede contactarte para futuros viajes."
              : "Your info has been saved and this customer can now contact you for future rides."}
          </p>
          <Link to="/" className="mt-6 inline-block" data-testid="ride-scan-done-cta"
            style={{ height: 48, lineHeight: "48px", padding: "0 24px",
                     background: "linear-gradient(180deg, #F5CC55, #E8B84B)",
                     color: "#0B0B0B", borderRadius: 12,
                     fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 700 }}>
            {lang === "es" ? "Explorar Habano" : "Explore Habano"}
          </Link>
        </section>
      ) : (
        // Signup form
        <section className="mx-5 mt-6" data-testid="ride-scan-form"
          style={{ background: "#171717", border: "1px solid rgba(232,184,75,0.3)",
                   borderRadius: 16, padding: "22px 22px 24px" }}>
          <div className="inline-flex items-center gap-2"
            style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#E8B84B",
                     letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 700 }}>
            <ShieldCheck className="w-4 h-4" strokeWidth={1.5} />
            {lang === "es" ? "Regístrate como conductor" : "Sign up as a driver"}
          </div>
          <p className="mt-2" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14,
                                        color: "#B0AAA3", lineHeight: 1.5 }}>
            {lang === "es"
              ? "Únete a Habano para recibir más clientes directamente en WhatsApp."
              : "Join Habano to receive more customers directly on WhatsApp."}
          </p>

          <label className="block mt-5 mb-2"
            style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#E8B84B",
                     letterSpacing: "0.04em", fontWeight: 600 }}
            htmlFor="ride-scan-name">
            {lang === "es" ? "Tu nombre" : "Your name"}
          </label>
          <input id="ride-scan-name" type="text" data-testid="ride-scan-name-input"
            value={name} onChange={(e) => setName(e.target.value)}
            placeholder={lang === "es" ? "Ej. Carlos Martínez" : "e.g. Carlos Martinez"}
            className="w-full outline-none"
            style={{ height: 52, background: "#0B0B0B", color: "#F5F1E8",
                     border: "1px solid rgba(232,184,75,0.3)", borderRadius: 12,
                     padding: "0 14px", fontFamily: "'DM Sans', sans-serif", fontSize: 16 }} />

          <label className="block mt-4 mb-2"
            style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#E8B84B",
                     letterSpacing: "0.04em", fontWeight: 600 }}
            htmlFor="ride-scan-whatsapp">
            {lang === "es" ? "Número de WhatsApp" : "WhatsApp number"}
          </label>
          <input id="ride-scan-whatsapp" type="tel" data-testid="ride-scan-whatsapp-input"
            value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)}
            placeholder="+53 5 555 0000"
            className="w-full outline-none"
            style={{ height: 52, background: "#0B0B0B", color: "#F5F1E8",
                     border: "1px solid rgba(232,184,75,0.3)", borderRadius: 12,
                     padding: "0 14px", fontFamily: "'DM Sans', sans-serif", fontSize: 16 }} />

          <button type="button" data-testid="ride-scan-submit" onClick={submit}
            disabled={submitting}
            className="w-full mt-6 inline-flex items-center justify-center gap-2"
            style={{ height: 56, background: "linear-gradient(180deg, #F5CC55, #E8B84B)",
                     color: "#0B0B0B", border: "none", borderRadius: 14,
                     fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 700,
                     opacity: submitting ? 0.5 : 1,
                     cursor: submitting ? "not-allowed" : "pointer" }}>
            {submitting
              ? <><Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />{lang === "es" ? "Registrando…" : "Signing up…"}</>
              : (lang === "es" ? "Registrarme y guardar cliente" : "Sign up and save customer")}
          </button>

          <p className="mt-3 text-center"
            style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#B0AAA3",
                     lineHeight: 1.5 }}>
            {lang === "es"
              ? "Al registrarte aceptas que tu información se comparta con este cliente."
              : "By signing up you agree to share your info with this customer."}
          </p>
        </section>
      )}
    </main>
  );
}
